import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { compose } from 'redux';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import withRouteHelpers from 'common/hoc/withRouteHelpers';
import useSeasons from 'studio/hooks/useSeasons';
import formatCurrency from 'studio/components/OfferPage/helpers/formatCurrency';
import useFeatureFlags from 'common/hooks/useFeatureFlags';

const styles = theme => ({
  root: {
    display: 'grid',
    height: '100%',
    gridTemplateRows: '60px 60px 30px minmax(166px, max-content) 64px',
  },
  headerContainer: {
    backgroundColor: theme.palette.secondary.main,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    textAlign: 'center',
    color: theme.palette.secondary.contrastText,
  },
  subHeader: {
    padding: theme.spacing.unit,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  lineItemContainer: {
    display: 'grid',
    gridAutoRows: 'auto',
    paddingBottom: 12,
  },
  lineItem: {
    display: 'grid',
    gridTemplateColumns: '40% 60%',
    paddingLeft: '35px',
    paddingRight: '35px',
  },
  label: {
    fontSize: '1.1rem',
  },
  value: {
    fontSize: '1.1rem',
  },
  submitContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: 12,
  },
  subText: {
    textAlign: 'center',
  },
});

const getCrewMemberNames = (crewMembers = {}) => {
  const crewMemberIds = Object.keys(crewMembers).filter(id => crewMembers[id]);
  if (crewMemberIds.length >= 5) return `${crewMemberIds.length} crew members`;
  const crewMemberNames = crewMemberIds
    .map(id => {
      const { profile = {} } = crewMembers[id] || {};
      const { firstName = '', lastName = '' } = profile || {};
      return `${firstName} ${lastName}`;
    })
    .join(', ');
  return crewMemberNames;
};

const OfferConfirmationModal = props => {
  const {
    formData = {},
    onSubmit,
    submitInProgress = false,
    classes = {},
    onClose,
    crew = {},
    projectId,
  } = props;
  const {
    offerDate = {},
    termsOfEmployment = {},
    allowances = {},
    termsOfHire,
    dealNotes = [],
  } = formData || {};
  const { startDate } = offerDate || {};
  const {
    occupation: { label: occupation },
    union: { label: union },
    workSchedule: { label: workSchedule, value: workScheduleCode },
    employmentClassification,
    jobDescription,
    season,
    hiringStatus,
  } = termsOfHire || {};
  const [dealNote = {}] = dealNotes || [];
  const { notes: dealNoteText = '' } = dealNote || {};
  const {
    idleLocationSeventhDayRatePerHour,
    idleLocationSixthDayRatePerHour,
    locationGuaranteedHours,
    locationRatePerHour,
    locationRatePerWeek,
    overtimeRatePerHour,
    studioGuaranteedHours,
    studioRatePerHour,
    studioRatePerWeek,
    guaranteedHours,
    ratePerHour,
    dailyRate,
    weeklyRate,
    rate,
    numberOfDaysPerWorkWeek,
    guaranteedHours6thDay,
    guaranteedHours7thDay,
    sixthDayPayMultiplication,
    seventhDayPayMultiplication,
    negotiatedContractId,
  } = termsOfEmployment || {};

  const {
    boxRentalAllowance: {
      amount: boxRentalAllowance = null,
      per: boxRentalAllowancePer,
    } = {},
    carAllowance: { amount: carAllowance = null, per: carAllowancePer } = {},
    computerRentalAllowance: {
      amount: computerRentalAllowance = null,
      per: computerRentalAllowancePer,
    } = {},
    housingAllowance: {
      amount: housingAllowance = null,
      per: housingAllowancePer,
    } = {},
    mobilePhoneAllowance: {
      amount: mobilePhoneAllowance = null,
      per: mobilePhoneAllowancePer,
    } = {},
    perDiemAllowance: {
      amount: perDiemAllowance = null,
      per: perDiemAllowancePer,
    } = {},
  } = allowances || {};

  const { data: seasons = [] } = useSeasons(projectId);
  const flags = useFeatureFlags();
  const isNonUnionScheduleAFlagActive = flags.includes('nonUnionSchedules');
  const isNonUnionScheduleCFlagActive = flags.includes('nonUnionScheduleC');

  const { label: seasonLabel = '' } =
    seasons.find(({ id: seasonId }) => seasonId === season) || {};
  const hiringStatusDescription = {
    full_time: 'Full Time',
    variable: 'Variable',
  };

  const nonUnionScheduleALabels =
    isNonUnionScheduleAFlagActive && guaranteedHours
      ? [
          {
            label: 'Apply Hours per Day to 6th Day',
            value: guaranteedHours6thDay ? 'Yes' : 'No',
          },
          {
            label: 'Apply Hours per Day to 7th Day',
            value: guaranteedHours7thDay ? 'Yes' : 'No',
          },
        ]
      : [];

  const nonUnionScheduleCLabels =
    isNonUnionScheduleCFlagActive && ['C', 'E', 'CN'].includes(workScheduleCode)
      ? [
          { label: 'Weekly Rate', value: rate, dollar: true },
          {
            label: 'Pay 6th Day at',
            value:
              sixthDayPayMultiplication > 0
                ? `${sixthDayPayMultiplication}x`
                : 'No additional pay',
          },
          {
            label: 'Pay 7th Day at',
            value:
              seventhDayPayMultiplication > 0
                ? `${seventhDayPayMultiplication}x`
                : 'No additional pay',
          },
        ]
      : [];

  let negotiatedContractTermsLabel = null;
  if (!!negotiatedContractId) {
    negotiatedContractTermsLabel = negotiatedContractId === 'N' ? 'No' : 'Yes';
  }

  const lineItems = [
    { label: 'Start Date', value: startDate },
    { label: 'Season', value: seasonLabel },
    { label: 'Union', value: union },
    { label: 'Occupation', value: occupation },
    { label: 'Work Schedule', value: workSchedule },
    { label: 'Job Description', value: jobDescription },
    {
      label: 'Employment Classification',
      value: (employmentClassification || '').toUpperCase(),
    },
    { label: 'Hiring Status', value: hiringStatusDescription[hiringStatus] },
    { label: 'Deal Notes', value: dealNoteText },
    {
      label: 'Use Negotiated Contract Terms',
      value: negotiatedContractTermsLabel,
    },
    { label: 'Studio - Rate Per Hour', value: studioRatePerHour, dollar: true },
    { label: 'Studio - Rate Per Week', value: studioRatePerWeek, dollar: true },
    { label: 'Studio - Guaranteed Hours', value: studioGuaranteedHours },
    {
      label: 'Rate Per Hour',
      value: ratePerHour ? formatCurrency(ratePerHour) : null,
      dollar: true,
    },
    {
      label: 'Overtime Rate Per Hour',
      value: overtimeRatePerHour,
      dollar: true,
    },
    { label: 'Hours Per Day', value: guaranteedHours },
    ...nonUnionScheduleALabels,
    ...nonUnionScheduleCLabels,
    { label: 'Daily Rate', value: dailyRate, dollar: true },
    { label: 'Weekly Rate', value: weeklyRate, dollar: true },
    { label: 'Number Of Days Per Work Week', value: numberOfDaysPerWorkWeek },
    {
      label: 'Distant - Rate Per Hour',
      value: locationRatePerHour,
      dollar: true,
    },
    {
      label: 'Distant - Rate Per Week',
      value: locationRatePerWeek,
      dollar: true,
    },
    { label: 'Distant - Guaranteed Hours', value: locationGuaranteedHours },
    {
      label: 'Distant - Idle Pay on Sixth Day',
      value: idleLocationSixthDayRatePerHour,
      dollar: true,
    },
    {
      label: 'Distant - Idle Pay on Seventh Day',
      value: idleLocationSeventhDayRatePerHour,
      dollar: true,
    },
    {
      label: 'Box Rental Allowance',
      value: boxRentalAllowance,
      per: boxRentalAllowancePer,
      dollar: true,
    },
    {
      label: 'Car Allowance',
      value: carAllowance,
      per: carAllowancePer,
      dollar: true,
    },
    {
      label: 'Computer Rental Allowance',
      value: computerRentalAllowance,
      per: computerRentalAllowancePer,
      dollar: true,
    },
    {
      label: 'Housing Allowance',
      value: housingAllowance,
      per: housingAllowancePer,
      dollar: true,
    },
    {
      label: 'Mobile Phone Allowance',
      value: mobilePhoneAllowance,
      per: mobilePhoneAllowancePer,
      dollar: true,
    },
    {
      label: 'Per Diem Allowance',
      value: perDiemAllowance,
      per: perDiemAllowancePer,
      dollar: true,
    },
    { label: 'Crew Members', value: getCrewMemberNames(crew) },
  ];

  const lineItemsToDisplay = lineItems.filter(({ value }) => !!value);

  return (
    <div className={classes.root}>
      <div className={classes.headerContainer}>
        <Typography variant="h4" className={classes.header}>
          Confirm Offer
        </Typography>
      </div>
      <Typography variant="h5" className={classes.subHeader}>
        Offer Terms
      </Typography>
      <Typography variant="subtitle1" className={classes.subText}>
        Are you sure you want to create this offer?
      </Typography>
      <div className={classes.lineItemContainer}>
        {lineItemsToDisplay
          .filter(({ value }) => !!value)
          .map(({ label, value, per, dollar }) => (
            <div className={classes.lineItem} key={`${label}-${value}`}>
              <Typography
                variant="body1"
                className={classes.label}
                data-test-id={`OfferConfirmationModal-label-${label}`}
              >
                {label}
              </Typography>
              <Typography
                variant="body1"
                className={classes.value}
                data-test-id={`OfferConfirmationModal-value-${label}${value}`}
              >
                {dollar ? '$' : ''}
                {dollar ? formatCurrency(value) : value}
                {per && ` / ${per}`}
              </Typography>
            </div>
          ))}
      </div>
      <div className={classes.submitContainer}>
        <Button
          variant="contained"
          color="primary"
          onClick={onClose}
          disabled={submitInProgress}
          data-test-id="OfferConfirmationModal-cancelButton"
        >
          No, Take me back
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={onSubmit}
          disabled={submitInProgress}
          data-test-id="OfferConfirmationModal-button"
        >
          {submitInProgress ? 'Creating Offer...' : 'Yes, Create Offer'}
        </Button>
      </div>
    </div>
  );
};

export default compose(
  withRouteHelpers,
  withStyles(styles),
)(OfferConfirmationModal);
